import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import React, { useEffect, useState } from 'react';
import Button from 'src/components/button';
import Loader from 'src/components/loader';
import { toastAlert } from 'src/utils/toastAlert';
import Service from 'src/setup/network';
import LogoUploadWithPreview from 'src/components/logo-upload-with-preview';
import ImageUploadWithPreview from 'src/components/image-upload-with-preview';
import { fetchDetails } from 'src/redux/action/details.action';
import { useDispatch } from 'react-redux';
import Address from './Address';
import TextField from 'src/components/text-field';
import MapContainer from 'src/components/Map';
import useFetch from 'src/hooks/useFetch';
import { LabelCustomSelect } from 'src/components/DiscountSelect/DiscountCustomSelect';

interface IProps {
  detailData?: any;
  loading?: boolean;
}

const FORM_VALIDATION = Yup.object().shape({
  primaryContactNumber: Yup.string()
    .matches(/(?:\(?\+977\)?)?[9][6-9]\d{8}/, 'Invalid Nepali phone number')
    .min(9, 'Primary Contact Number must be at least 9 characters')
    .max(10, 'Primary Contact Number must be at most 10 characters')
    .nullable(),
  secondaryContactNumber: Yup.string()
    .matches(
      /(?:\(?\+977\)?)?[9][6-9]\d{8}|01[-]?[0-9]{7}/,
      'Invalid Nepali phone number'
    )
    .min(9, 'Secondary Contact Number must be at least 9 characters')
    .max(10, 'Secondary Contact Number must be at most 10 characters')
    .nullable()
    .test(
      'not-equal',
      'Secondary contact number should not be the same as primary',
      function (value) {
        const primaryContactNumber = this.resolve(
          Yup.ref('primaryContactNumber')
        );
        return value !== primaryContactNumber;
      }
    ),
  email: Yup.string().email('Invalid email address').nullable(),
});

const About: React.FC<IProps> = ({ detailData, loading }) => {
  const { data: attributeData } = useFetch('/attributes');
  const selectedAttributes = detailData?.attributes;
  const attributes = attributeData?.attributes;
  const about = detailData.about;
  const address = detailData;
  const [previewImage, setPreviewImage] = useState<any>('');
  const [previewLogo, setPreviewLogo] = useState<any>('');

  const [latitude, setLatitude] = useState<any>(
    address?.address?.coordinates?.latitude
  );
  const [longitude, setLongitude] = useState<any>(
    address?.address?.coordinates?.longitude
  );

  const dispatch = useDispatch();

  const initValues: any = {
    description: about ? about?.description : '',
    coverImage: about ? about?.coverImage : '',
    logo: about ? about?.logo : '',
    area: address ? address?.address?.area : '',
    city: address ? address?.address?.city : '',
    primaryContactNumber: address ? address?.contactDetails?.primaryPhone : '',
    secondaryContactNumber: address
      ? address?.contactDetails?.secondaryPhone
      : '',
    email: address ? address?.contactDetails?.primaryEmail : '',
    latitude: address ? address?.address?.coordinates?.latitude : '',
    longitude: address ? address?.address?.coordinates?.longitude : '',
  };

  if (selectedAttributes) {
    for (let item of selectedAttributes) {
      initValues[item.id] = item.types;
    }
  }

  const handleSubmit = async (values: any, { resetForm }: any) => {
    const payload = {
      about: {
        description: values?.description,
        logo: values?.logo,
        coverImage: values?.image,
      },
      address: {
        city: values?.city,
        area: values?.area,
        coordinates: {
          latitude: latitude,
          longitude: longitude,
        },
      },
      contactDetails: {
        primaryPhone: values?.primaryContactNumber,
        secondaryPhone: values?.secondaryContactNumber,
        primaryEmail: values?.email,
      },
      attributes: attributes.map((attribute: any, index: number) => {
        return {
          id: attribute?.id,
          title: attribute?.title,
          types: values[attribute?.id] || [],
        };
      }),
    };

    try {
      await Service.post('/settings/update', payload);
      toastAlert('success', `Resturant Profile is successfully updated`);
      dispatch(fetchDetails() as any);
    } catch (err: any) {
      toastAlert('error', err?.response?.data?.message);
    }
  };

  useEffect(() => {
    setPreviewImage(about ? about?.coverImage : '');
    setPreviewLogo(about ? about?.logo : '');
  }, [detailData]);
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Formik
          initialValues={initValues}
          validateOnMount
          onSubmit={handleSubmit}
          validationSchema={FORM_VALIDATION}
        >
          {({ isSubmitting, isValid, setFieldValue }) => (
            <Form className="flex gap-6 flex-wrap relative rounded-xl border border-border-color-dark p-6 2xl:w-[calc(388px*2+6rem)]">
              {/* required for future use  */}
              <div className="h-full relative flex flex-col gap-4 w-[calc(50%-1rem)] 2xl:w-[388px]">
                <h2 className="text-base font-semibold">General Information</h2>
                <LogoUploadWithPreview
                  label="Upload Logo"
                  previewImage={previewLogo}
                  setPreviewImage={setPreviewLogo}
                  setFieldValue={setFieldValue}
                />
                <ImageUploadWithPreview
                  label="Upload Cover Image"
                  previewImage={previewImage}
                  setPreviewImage={setPreviewImage}
                  setFieldValue={setFieldValue}
                />
                <label className="flex flex-col gap-2 text-md">
                  Description
                  <textarea
                    className="border-[1px] border-light-gray rounded-xl px-1 py-2 focus:border-[1px] focus:border-primary-200 focus:shadow-none focus:ring-0 focus:outline-none"
                    rows={4}
                    cols={36}
                    name="description"
                    defaultValue={about ? about?.description : ''}
                    onChange={(e) =>
                      setFieldValue('description', e.target.value)
                    }
                  ></textarea>
                </label>

                <div className="mt-2">
                  <h2 className="text-base font-semibold mb-4">
                    Additional Information
                  </h2>
                  <FieldArray
                    name="attributes"
                    render={(arrayHelpers) => (
                      <>
                        {attributes?.map((attribute: any, index: number) => (
                          <Field
                            key={attribute?.id}
                            name={attribute?.id}
                            options={attribute?.types.map(
                              (type: string, index: number) => {
                                return { name: type, id: type };
                              }
                            )}
                            className={'mt-3'}
                            component={LabelCustomSelect}
                            isMulti={true}
                            label={attribute?.title}
                            placeholder={'Select ' + attribute?.title}
                            defaultValue={
                              selectedAttributes
                                .find((obj: any) => obj?.id === attribute?.id)
                                ?.types.map((type: string, index: number) => {
                                  return { label: type, value: type };
                                })
                              // selectedAttributes?.map(
                              //   (option: any, index: number) => {
                              //     return option?.id === attribute?.id &&
                              //       option.type
                              //       ? {
                              //           label: option.type,
                              //           value: option.type,
                              //         }
                              //       : undefined;
                              //   }
                              // )
                            }
                          />
                        ))}
                      </>
                    )}
                  />
                </div>
              </div>
              <div className="w-[calc(50%-1rem)] 2xl:w-[388px] flex flex-col justify-between ">
                <div className="sticky top-0 ">
                  <h2 className="text-base font-semibold mb-4">
                    Contact Details
                  </h2>
                  <TextField label={'Area'} name="area" />
                  <TextField label={'City'} name="city" />

                  {/* uncomment this when order within certain radius is needed  */}

                  <MapContainer
                    showMarker={true}
                    address={address}
                    radius={0}
                    latitude={latitude}
                    setLatitude={setLatitude}
                    longitude={longitude}
                    setLongitude={setLongitude}
                  />
                  <TextField
                    label={'Primary Contact Number'}
                    name="primaryContactNumber"
                    type="tel"
                  />
                  <TextField
                    label={'Landline/Secondary Contact Number'}
                    name="secondaryContactNumber"
                    type="tel"
                  />
                  <TextField label={'Email'} name="email" />
                </div>
              </div>
              <div className="flex items-center justify-end my-2 mx-5 w-full">
                <Button
                  text="save details"
                  variant="primary"
                  type="submit"
                  isSubmitting={isSubmitting}
                  isValid={isValid}
                />
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default About;
